import React from 'react';
import { IStandardDocumentData, IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { IUserData } from 'types/users/IUser';
import { eDocumentVisibilityType } from 'types/order/documents/eDocumentVisibilityType';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { SelectDocumentToMerge } from './SelectDocumentToMerge';
import { BodyText, Button, CheckboxInput, FlexRow, SmallBodyText, SpinnerSize, Tooltip } from 'ui-toolkit/components';
import { faDownload, faFolderPlus } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { DateFormat, getDateString } from '../../../../ui-toolkit/utilities/getDateString';
import { getUserName } from 'utilities/users/getUserName';
import { DocumentActivityCell } from './DocumentActivityCell/DocumentActivityCell';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { downloadDocument } from 'API/order/documents/downloadDocument';
import { SpinnerTW } from '../../../../tailwindComponents/spinnerTW/SpinnerTW';

export interface GenerateStandardDocumentsTableColumnsProps {
  onFundingClick: (documentId: string, versionId: string, funding: boolean) => void;
  onPublish: (documentId: string, visibilityType: eDocumentVisibilityType) => void;
  onUnpublish: (documentId: string, visibilityType: eDocumentVisibilityType) => void;
  onSigningClick: (documentId: string, versionId: string, signing: boolean) => void;
  setDeleteDocument: (val: IStandardDocumentVersionData | null) => void;
  setPreviewDoc: (val: IStandardDocumentVersionData | null) => void;
  setShowGenerateLinkModal: (val: IStandardDocumentVersionData | null) => void;
  users: IUserData[];
  onFinalizeDocument: (docId: string, versionId: string) => void;
  setDocToFinalize: (val: IStandardDocumentData | null) => void;
  getCategoryLabel: (categoryName: string) => string;
  getCategoryDescription: (categoryName: string) => string | undefined;
  onDetailsClick: (document: IStandardDocumentVersionData) => void;
  isZillowClosingEnabled: boolean;
}

const PublishColumnStyle = styled(FlexRow)`
  flex-direction: column;
  > * {
    margin-left: 0;
    white-space: nowrap !important;
  }
`;

export interface StandardDocumentVersionDataWithZDocLoadingData extends IStandardDocumentVersionData {
  isZDocLoading: boolean;
}

export const generateStandardDocumentsTableColumns = ({
  onFundingClick,
  onPublish,
  onSigningClick,
  onUnpublish,
  setDeleteDocument,
  setPreviewDoc,
  setShowGenerateLinkModal,
  users,
  onFinalizeDocument,
  setDocToFinalize,
  onDetailsClick,
  getCategoryLabel,
  getCategoryDescription,
  isZillowClosingEnabled,
}: GenerateStandardDocumentsTableColumnsProps): IBootstrapTableColumnProps<StandardDocumentVersionDataWithZDocLoadingData>[] => [
  {
    text: '',
    dataField: 'id',
    formatter: (_, row) => <SelectDocumentToMerge document={row} />,
    headerAttrs: { width: '38' },
    sort: true,
  },
  {
    text: '',
    dataField: '',
    headerAttrs: { width: '38' },
    formatter: (_, row) => {
      return (
        <Tooltip tooltipContent={'Download'} trigger={'hover'} placement={'bottom'}>
          <Button
            type={'button'}
            isInline
            intent={'ghost'}
            onClick={() => downloadDocument(row.id, row.versionId, row.fileName)}
            disabled={determineIsDisabledWhenZDocLoading(row)}
          >
            <FontAwesomeIcon icon={faDownload} style={{ padding: '6px' }} />
          </Button>
        </Tooltip>
      );
    },
  },
  {
    text: 'File',
    dataField: 'fileName',
    sort: true,
    formatter: (_, row) => <FileNameComponent row={row} setPreviewDoc={setPreviewDoc} />,
  },
  {
    text: 'Category',
    dataField: 'category',
    sort: true,
    formatter: (_, row) => {
      const label = getCategoryLabel(row.category);
      const description = getCategoryDescription(row.category) ?? 'No description available.';
      return (
        <>
          {label && (
            <BodyText data-test-id={'document-table-category-column-label'}>
              {label}
              <Tooltip
                data-test-id={'document-table-category-column-tooltip'}
                tooltipContent={<SmallBodyText style={{ width: '217px' }}>{description}</SmallBodyText>}
                trigger={'hover'}
                placement={'bottom'}
              >
                <FontAwesomeIcon icon={faInfoCircle} style={{ paddingLeft: '6px' }} />
              </Tooltip>
            </BodyText>
          )}
        </>
      );
    },
  },
  {
    dataField: 'fileUpdatedAt',
    formatter: (_, row) => <BodyText>{getDateString(DateFormat.hugeDateTime, row.fileUpdatedAt, true)}</BodyText>,
    text: 'Updated At',
    sort: true,
  },
  {
    text: 'Updated By',
    dataField: 'uploaderType',
    formatter: (_, row) => row && <BodyText>{getUserName(users, isZillowClosingEnabled, row.uploaderType, row.uploaderId)}</BodyText>,
    sort: true,
  },
  {
    text: 'Client',
    dataField: 'clientPublishedAt',
    formatter: (_, row) => (
      <PublishColumnStyle
        justifyContent={'center'}
        verticalAlign={row.clientPublishedAt ? 'flex-start' : 'center'}
      >
        <>
          {row.clientPublishedAt ? (
            <>
              <SmallBodyText>
                {getUserName(users, isZillowClosingEnabled, row.clientPublisherType, row.clientPublisherId)}
              </SmallBodyText>
              <SmallBodyText>
                {getDateString(DateFormat.atShortDateTime, row.clientPublishedAt, true)}
              </SmallBodyText>
              <Button
                type={'button'}
                isInline
                intent={'ghost'}
                onClick={() => onUnpublish(row.id, eDocumentVisibilityType.Client)}
                disabled={determineIsDisabledWhenZDocLoading(row)}
              >
                Unpublish
              </Button>
            </>
          ) : (
            <Tooltip tooltipContent={'Publish Client'} trigger={'hover'} placement={'bottom'}>
              <Button
                type={'button'}
                isInline
                intent={'ghost'}
                onClick={() => onPublish(row.id, eDocumentVisibilityType.Client)}
                disabled={determineIsDisabledWhenZDocLoading(row)}
              >
                <FontAwesomeIcon icon={faPaperPlane} style={{ padding: '6px' }} />
              </Button>
            </Tooltip>
          )}
        </>
      </PublishColumnStyle>
    ),
    sort: true,
  },
  {
    text: 'Vendor',
    dataField: 'vendorPublishedAt',
    formatter: (_, row) => (
      <PublishColumnStyle justifyContent={'center'} verticalAlign={row.vendorPublishedAt ? 'flex-start' : 'center'}>
        {row.vendorPublishedAt ? (
          <>
            <SmallBodyText>{getUserName(users, isZillowClosingEnabled, row.vendorPublisherType, row.vendorPublisherId)}</SmallBodyText>
            <SmallBodyText>{getDateString(DateFormat.atShortDateTime, row.vendorPublishedAt, true)}</SmallBodyText>
            <Button
              type={'button'}
              isInline
              intent={'ghost'}
              onClick={() => onUnpublish(row.id, eDocumentVisibilityType.Vendor)}
              disabled={determineIsDisabledWhenZDocLoading(row)}
            >
              Unpublish
            </Button>
          </>
        ) : (
          <Tooltip tooltipContent={'Publish Vendor'} trigger={'hover'} placement={'bottom'}>
            <Button
              type={'button'}
              isInline
              intent={'ghost'}
              onClick={() => onPublish(row.id, eDocumentVisibilityType.Vendor)}
              disabled={determineIsDisabledWhenZDocLoading(row)}
            >
              <FontAwesomeIcon icon={faPaperPlane} style={{ padding: '6px' }} />
            </Button>
          </Tooltip>
        )}
      </PublishColumnStyle>
    ),
    sort: true,
  },
  {
    text: 'Signing',
    dataField: 'requiredAtSigning',
    formatter: (_, row) => (
      <FlexRow justifyContent={'center'}>
        <CheckboxInput
          value={row.requiredAtSigning}
          onChange={(val) => onSigningClick(row.id, row.versionId, val)}
          disabled={determineIsDisabledWhenZDocLoading(row)}
        />
      </FlexRow>
    ),
    sort: true,
  },
  {
    text: 'Funding',
    dataField: 'funding',
    formatter: (_, row) => (
      <FlexRow justifyContent={'center'}>
        <CheckboxInput
          value={row.funding}
          onChange={(val) => onFundingClick(row.id, row.versionId, val)}
          disabled={determineIsDisabledWhenZDocLoading(row)}
        />
      </FlexRow>
    ),
    sort: true,
  },
  {
    text: '',
    dataField: 'versioned',
    formatter: (_, row) => {
      if (determineIsDisabledWhenZDocLoading(row)) {
        return null;
      }
      return (
        <DocumentActivityCell
          document={row}
          onShowDelete={() => setDeleteDocument(row)}
          setShowGenerateLinkModal={setShowGenerateLinkModal}
          onFinalizeDocument={onFinalizeDocument}
          setDocToFinalize={setDocToFinalize}
          onDetailsClick={() => onDetailsClick(row)}
        />
      );
    },
    sort: true,
  },
];

const MergedIcon = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.colors.primary};
`;

const specialZDocsCategories: Set<string> = new Set(['zdocs-spruce-buyer-affidavits-unexecuted']);

const determineIsDisabledWhenZDocLoading = (row: StandardDocumentVersionDataWithZDocLoadingData) => {
  return specialZDocsCategories.has(row.category) && row.isZDocLoading;
};

const FileNameComponent: React.FC<{
  row: StandardDocumentVersionDataWithZDocLoadingData;
  setPreviewDoc: (val: StandardDocumentVersionDataWithZDocLoadingData | null) => void;
}> = ({ row, setPreviewDoc }) => {
  const isDisabledWhenZDocLoading = determineIsDisabledWhenZDocLoading(row);
  return (
    <Button intent={'ghost'} onClick={() => setPreviewDoc(row)} isInline disabled={isDisabledWhenZDocLoading}>
      {!!row.metadata && !!row.metadata.merged_documents && row.metadata.merged_documents.length > 0 && (
        <MergedIcon icon={faFolderPlus} title="Combined Document" />
      )}
      <FileNameButtonContent row={row} isDisabled={isDisabledWhenZDocLoading} />
    </Button>
  );
};

const FileNameButtonContent: React.FC<{ isDisabled: boolean; row: StandardDocumentVersionDataWithZDocLoadingData }> = ({
  isDisabled,
  row,
}) => {
  if (!isDisabled) {
    return <div>{row.fileName}</div>;
  }
  return (
    <div className={'flex flex-row items-center gap-2'}>
      <SpinnerTW size={SpinnerSize.tiny} />
      This ZDoc is regenerating
    </div>
  );
};
